import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import DataTable from '../../components/ui/data-table';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { CreditCard, Download, Calendar, AlertCircle } from 'lucide-react';

const PaymentsPage = () => {
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      // TODO: Replace with actual API call
      // const res = await parentAPI.getPayments();
      // setPayments(res.data.data || []);
      
      // Placeholder data for now
      setPayments([]);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchPayments();
  }, []);

  const columns = [
    { id: 'date', header: 'Date', accessorKey: 'date', sortable: true },
    { id: 'description', header: 'Description', accessorKey: 'description' },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', sortable: true },
    { id: 'status', header: 'Status', accessorKey: 'status', sortable: true },
    { id: 'method', header: 'Payment Method', accessorKey: 'method' },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline">
            <Download className="w-4 h-4 mr-1" />
            Receipt
          </Button>
        </div>
      )
    }
  ];

  const paymentSummary = [
    { id: 'total', name: 'Total Paid', amount: '$0.00', color: 'text-green-600' },
    { id: 'pending', name: 'Pending', amount: '$0.00', color: 'text-yellow-600' },
    { id: 'overdue', name: 'Overdue', amount: '$0.00', color: 'text-red-600' },
    { id: 'next', name: 'Next Payment', amount: '$0.00', color: 'text-blue-600' }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchPayments}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Fee Payments</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Payment Schedule
          </Button>
          <Button size="sm">
            <CreditCard className="w-4 h-4 mr-2" />
            Make Payment
          </Button>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {paymentSummary.map((summary) => (
          <Card key={summary.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{summary.name}</p>
                  <p className={`text-2xl font-bold ${summary.color}`}>{summary.amount}</p>
                </div>
                <CreditCard className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          {(!payments || payments.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No payment history found</p>
              <p className="text-sm">Payment records will appear here once payments are made</p>
            </div>
          ) : (
            <DataTable data={payments} columns={columns} searchable />
          )}
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No payment methods configured</p>
            <p className="text-sm">Add a payment method to make payments easier</p>
            <Button className="mt-4" variant="outline">
              Add Payment Method
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
